import { Injectable, ComponentFactory, NgModule, Compiler, ModuleWithComponentFactories } from '@angular/core';
import { Widget } from '../models/Widget';
import { Subject } from 'rxjs/Subject';
import * as _ from 'lodash';

@Injectable()
export class DashboardsWidgetService {
    private widgets: Widget[];

    constructor () {
        this.widgets = [];
    }

    /**
     * Registers a widget into a global widgets collection.
     * @param widget The widget to register.
     */
    register(widget: Widget): void {
        if (this.exists(widget)) {
            // already registered
            throw new Error(`Widget ${widget.type} already registered.`);
        }

        if (!widget.component) {
            throw new Error(`Widget ${widget.type} does not have a main component.`);
        }

        if (!widget.editComponent) {
            throw new Error(`Widget ${widget.type} does not have an edit component.`);
        }

        if (!widget.configuration) {
            throw new Error(`Widget ${widget.type} does not have a configuration object.`);
        }

        this.widgets.push(widget);
    }

    /**
     * Removes a widget from the global widgets collection.
     * @param widget The widget to unregister.
     */
    unregister(widget: Widget): void {
        if (!this.exists(widget)) {
            // not registered
            throw new Error(`Widget ${widget.type} not registered.`);
        }

        _.remove(this.widgets, (w: Widget) => {
            return w.type === widget.type;
        });
    }

    /**
     * Removes all widgets from the global widgets collection.
     */
    unregisterAll(): void {
        this.widgets.splice(0);
    }

    /**
     * Retrieves a registered widget object based on its type.
     * @param type The type of the widget to get.
     */
    getWidget(type: string): Widget {
        return _.find(this.widgets, (w: Widget) => {
            return w.type === type;
        });
    }

    /**
     * Retrieves all registered widget objects.
     */
    getAllWidgets(): Widget[] {
        // return a clone of the original array
        return _.cloneDeep(this.widgets);
    }

    /**
     * Retrieves the number of all registered wdigets.
     */
    count(): number {
        return this.widgets.length;
    }

    private exists(widget: Widget): boolean {
        return _.find(this.widgets, (w: Widget) => {
            return w.type === widget.type;
        }) !== undefined;
    }
}
